import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { useTenantData } from "contexts/tenant";
import { useTenantTranslation } from "hooks/formatters";
import { useFormatNumber } from "src/utils/formatNumber";
import { useLayerWeightFormat } from "./useLayerWeightFormat";
import { useMaterialColor } from "hooks/useMaterials";
import React from "react";
import { MaterialRead } from "src/store/api/generatedApi";
import { LayerBar, LayerEditorSize } from "./LayerBar";
import { BasketLayerState } from "./types";
import {
  basketDropId,
  basketEndLabelKeys,
  visualLayers,
} from "./layerEditorState";

type AddLayerState =
  | { phase: "idle" }
  | { phase: "pickMaterial" }
  | { phase: "enterWeight"; materialName: string };

interface Props {
  basket: BasketLayerState;
  materials: MaterialRead[];
  onLayerWeightChange: (layerId: string, weight: number) => void;
  onLayerDelete: (layerId: string) => void;
  onLayerSplit: (layerId: string) => void;
  onAddLayer: (
    basketNumber: number,
    materialName: string,
    weight: number
  ) => void;
  weightsEditable: boolean;
  massScaleFactor: number;
  size: LayerEditorSize;
}

export const BasketColumn = ({
  basket,
  materials,
  onLayerWeightChange,
  onLayerDelete,
  onLayerSplit,
  onAddLayer,
  weightsEditable,
  massScaleFactor,
  size,
}: Props) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();
  const { getColor } = useMaterialColor();
  const [addLayerState, setAddLayerState] = React.useState<AddLayerState>({
    phase: "idle",
  });
  const [draftWeight, setDraftWeight] = React.useState("");
  const { parse: parseWeight, unitLabel } =
    useLayerWeightFormat(massScaleFactor);
  const { basket_layer_display_order } = useTenantData();
  const displayedLayers = visualLayers(
    basket.layers,
    basket_layer_display_order
  );
  const layerFlexDirection =
    basket_layer_display_order === "top_first" ? "column" : "column-reverse";
  const endLabelKeys = basketEndLabelKeys(basket_layer_display_order);
  const endLabelSx = {
    textAlign: "center" as const,
    color: theme.palette.text.disabled,
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
  };

  const { setNodeRef } = useDroppable({
    id: basketDropId(basket.basketNumber),
  });

  const totalWeight =
    basket.layers.reduce((sum, l) => sum + l.weight, 0) * massScaleFactor;
  const fullnessLabel =
    basket.fullness === null
      ? null
      : `${formatNumber(0, true)(basket.fullness * 100)}% ${t("full")}`;

  const availableMaterials = materials.filter(
    (m) => m.addition_location === "basket"
  );

  const cancelAddLayer = () => {
    setAddLayerState({ phase: "idle" });
    setDraftWeight("");
  };

  const commitNewLayer = () => {
    if (addLayerState.phase !== "enterWeight") return;
    const result = parseWeight(draftWeight);
    if (result.valid && result.value > 0) {
      onAddLayer(
        basket.basketNumber,
        addLayerState.materialName,
        result.value / massScaleFactor
      );
    }
    cancelAddLayer();
  };

  return (
    <Box
      ref={setNodeRef}
      data-testid={`basket-column-${basket.basketNumber}`}
      sx={{
        flex: 1,
        minWidth: 200,
        display: "flex",
        flexDirection: "column",
        border: `2px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: theme.palette.grey[50],
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant={size === "comfortable" ? "h5" : "subtitle2"}
          fontWeight={size === "comfortable" ? undefined : "fontWeightBold"}
        >
          {t("basket")} {basket.basketNumber}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          {basket.fullness !== null && (
            <Chip
              label={fullnessLabel}
              size={size === "comfortable" ? "medium" : "small"}
              variant="outlined"
              color={basket.fullness > 1 ? "error" : "default"}
            />
          )}
          <Chip
            label={`${formatNumber(0, true)(totalWeight)}${unitLabel}`}
            size={size === "comfortable" ? "medium" : "small"}
            variant="outlined"
          />
        </Stack>
      </Stack>
      <Typography variant="caption" sx={{ ...endLabelSx, pt: 0.5 }}>
        {t(endLabelKeys.first)}
      </Typography>
      <SortableContext
        items={displayedLayers.map((layer) => layer.id)}
        strategy={verticalListSortingStrategy}
      >
        <Stack
          spacing={0.5}
          sx={{
            px: 1,
            py: 0.5,
            flex: 1,
            flexDirection: layerFlexDirection,
            overflow: "auto",
            minHeight: 80,
          }}
        >
          {basket.layers.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.disabled }}
              >
                {t("noContents")}
              </Typography>
            </Box>
          ) : (
            basket.layers.map((layer) => (
              <LayerBar
                key={layer.id}
                layer={layer}
                color={getColor(layer.materialName)}
                onWeightChange={onLayerWeightChange}
                onDelete={onLayerDelete}
                onSplit={onLayerSplit}
                weightsEditable={weightsEditable}
                massScaleFactor={massScaleFactor}
                size={size}
              />
            ))
          )}
        </Stack>
      </SortableContext>
      <Typography variant="caption" sx={{ ...endLabelSx, pb: 0.5 }}>
        {t(endLabelKeys.last)}
      </Typography>
      {weightsEditable && (
        <Stack
          sx={{
            px: 1,
            pb: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            pt: 0.5,
          }}
        >
          {addLayerState.phase === "pickMaterial" && (
            <Autocomplete
              size="small"
              options={availableMaterials}
              getOptionLabel={(m) => m.name}
              onChange={(_, material) => {
                if (material) {
                  setAddLayerState({
                    phase: "enterWeight",
                    materialName: material.name,
                  });
                }
              }}
              onBlur={() => {
                setAddLayerState((current) =>
                  current.phase === "pickMaterial" ? { phase: "idle" } : current
                );
              }}
              renderInput={(params) => (
                <TextField {...params} autoFocus placeholder={t("addLayer")} />
              )}
              openOnFocus
            />
          )}
          {addLayerState.phase === "enterWeight" && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{
                px: 1,
                py: 0.5,
                bgcolor: alpha(getColor(addLayerState.materialName), 0.1),
                borderRadius: 1,
                border: `1px dashed ${getColor(addLayerState.materialName)}`,
                borderLeft: `4px solid ${getColor(addLayerState.materialName)}`,
              }}
            >
              <IconButton
                size="small"
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => {
                  setAddLayerState({ phase: "pickMaterial" });
                  setDraftWeight("");
                }}
                aria-label={t("cancel")}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
              <Typography
                variant={size === "comfortable" ? "h5" : "body2"}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  ...(size === "compact" && {
                    fontWeight: "fontWeightSemiBold",
                  }),
                }}
              >
                {addLayerState.materialName}
              </Typography>
              <TextField
                size="small"
                autoFocus
                value={draftWeight}
                onChange={(event) => {
                  setDraftWeight(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === "Tab") {
                    event.stopPropagation();
                    commitNewLayer();
                  } else if (event.key === "Escape") {
                    event.stopPropagation();
                    cancelAddLayer();
                  }
                }}
                onBlur={commitNewLayer}
                placeholder={unitLabel}
                sx={{ width: size === "comfortable" ? 120 : 90 }}
                slotProps={{
                  htmlInput: {
                    inputMode: "decimal",
                    "data-testid": `add-layer-weight-basket-${basket.basketNumber}`,
                  },
                }}
              />
            </Stack>
          )}
          {addLayerState.phase === "idle" && (
            <IconButton
              size="small"
              onClick={() => {
                setAddLayerState({ phase: "pickMaterial" });
              }}
              data-testid={`add-layer-basket-${basket.basketNumber}`}
            >
              <AddRoundedIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )}
    </Box>
  );
};
